import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Loader2, ShieldAlert, X, ExternalLink } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/api";

function embedYoutube(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    const id = u.searchParams.get("v");
    if (id) return `https://www.youtube.com/embed/${id}`;
  } catch { /* ignore */ }
  return url;
}
function embedVimeo(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/\//g, "");
      return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* ignore */ }
  return url;
}

/**
 * Admin-only lightweight video preview modal.
 * Uses the same signed-token pipeline as the learner player but without
 * watermarks / progress heartbeat.
 */
export function AdminVideoPreviewDialog({ open, onOpenChange, lesson }) {
  const videoRef = useRef(null);
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !lesson?.id) return undefined;
    let stopped = false;
    setPayload(null); setErr(null);
    (async () => {
      try {
        const { data } = await api.get(`/admin/lessons/${lesson.id}/video-preview`);
        if (!stopped) setPayload(data.data);
      } catch (e) { if (!stopped) setErr(formatApiError(e)); }
    })();
    return () => { stopped = true; };
  }, [open, lesson?.id]);

  useEffect(() => {
    if (!payload || payload.source_type === "external_url") return undefined;
    const v = videoRef.current; if (!v) return undefined;
    const abs = (p) => (p.startsWith("http") ? p : `${API_BASE.replace(/\/api$/, "")}${p}`);
    let hls;
    if (payload.hls_url && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(abs(payload.hls_url));
      hls.attachMedia(v);
    } else if (payload.hls_url && v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = abs(payload.hls_url);
    } else if (payload.playback_url) {
      v.src = abs(payload.playback_url);
    }
    return () => { if (hls) { try { hls.destroy(); } catch { /* ignore */ } } };
  }, [payload]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" data-testid="admin-video-preview-modal">
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-slate-900">{payload?.title || lesson?.lesson_title}</div>
            {payload?.file_name && <div className="truncate text-[11px] text-slate-500">{payload.file_name}</div>}
          </div>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="admin-video-preview-close"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="bg-black">
          {err ? (
            <div className="flex items-center gap-2 p-6 text-sm text-rose-100">
              <ShieldAlert className="h-4 w-4" /> {err}
            </div>
          ) : !payload ? (
            <div className="flex items-center justify-center p-10 text-slate-300">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading preview…
            </div>
          ) : payload.source_type === "external_url" && payload.external_url ? (
            <div className="aspect-video">
              <iframe
                src={
                  payload.external_url.includes("youtube") || payload.external_url.includes("youtu.be")
                    ? embedYoutube(payload.external_url)
                    : payload.external_url.includes("vimeo")
                      ? embedVimeo(payload.external_url)
                      : payload.external_url
                }
                className="h-full w-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title={payload.title}
              />
            </div>
          ) : (
            <video ref={videoRef} controls className="aspect-video w-full" data-testid="admin-video-preview-element" />
          )}
        </div>
        {payload && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 p-3 text-[11px] text-slate-500">
            <div className="flex flex-wrap items-center gap-3">
              <span><b className="text-slate-700">Type:</b> {payload.source_type || "device"}</span>
              {payload.duration_seconds > 0 && <span><b className="text-slate-700">Duration:</b> {Math.round(payload.duration_seconds / 60)} min</span>}
              {payload.file_size > 0 && <span><b className="text-slate-700">Size:</b> {(payload.file_size / 1024 / 1024).toFixed(1)} MB</span>}
            </div>
            {payload.source_type === "external_url" && payload.external_url && (
              <a href={payload.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#101876] hover:underline">
                Open source <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
